import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { rsdFormsSaga } from './saga';

export interface Moment {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
}

export interface MomentType {
  id: string;
  name: string;
  description?: string;
}

export interface FormField {
  id: string;
  name: string;
  label: string;
  type:
    | string
    | 'text'
    | 'select'
    | 'api_dropdown'
    | 'datepicker'
    | 'dateprangepicker'
    | 'switch'
    | 'checkbox'
    | 'richtext'
    | 'dropdown_richtext'
    | 'repeatable'
    | 'spacer'
    | 'multi_select'
    | 'multi_select_api'
    | 'custom_component';
  required: boolean;
  disabled?: boolean;
  // Optional flag to control UI visibility without affecting value/state.
  // When false, the field stays mounted and participates in state/payload,
  // but is visually hidden in the form.
  showField?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  section?: string;
  subType?: string;
  api?: string;
  defaultValue?: any;
  // Optional character limit for rich text fields. Use null/undefined for unlimited.
  charLimit?: number | null;
  // Optional maximum character length for text input fields.
  maxLength?: number;
  // Optional field-level column override. When provided, it indicates the
  // number of columns this field assumes for layout purposes within its section.
  // Example: in a section with columns=2, field.columns=2 renders half-width;
  // field.columns=1 renders full-width.
  columns?: number;
  sectionConfig?: {
    columns: number;
    // Whether the entire section is required (e.g., at least one field must be filled)
    required?: boolean;
    subheading?: string;
    toolTipMessage?: string;
  };
  dependsOn?: string;
  dependents?: string[];
  actions?: string[];
  minItems?: number;
  maxItems?: number;
  itemTemplate?: FormField;
  fieldGroup?: FormField[];
  /** When true, the group title renders above the grey group box (see GroupComponentField). */
  displayLabelOutside?: boolean;
  disableNA?: boolean;
  toolTipMessage?: string;
  // Optional flag to indicate grouped dropdown display (e.g., category headers)
  grouped?: boolean;
  // Optional subtitle text to display below the label
  subtitle?: string;
  /** Input-group prefix before the text field (UI only). String = label; true = use inputPrependLabel. */
  inputPrepend?: boolean | string;
  inputPrependLabel?: string;
}

export interface RsdFormsState {
  moments: Moment[];
  momentTypes: MomentType[];
  // Cache form fields by moment type ID
  formFieldsCache: Record<string, FormField[]>;
  formFields: FormField[];
  selectedMomentType: string | null;
  // Add allFormConfigs to cache all form configs from API
  allFormConfigs: Record<string, any> | null;
  loading: {
    moments: boolean;
    momentTypes: boolean;
    formFields: boolean;
    allFormConfigs: boolean;
  };
  error: {
    moments: string | null;
    momentTypes: string | null;
    formFields: string | null;
    allFormConfigs: string | null;
  };
}

export const initialState: RsdFormsState = {
  moments: [],
  momentTypes: [],
  formFieldsCache: {},
  formFields: [],
  selectedMomentType: null,
  allFormConfigs: null,
  loading: {
    moments: false,
    momentTypes: false,
    formFields: false,
    allFormConfigs: false
  },
  error: {
    moments: null,
    momentTypes: null,
    formFields: null,
    allFormConfigs: null
  }
};

const rsdFormsSlice = createSlice({
  name: 'rsdForms',
  initialState,
  reducers: {
    // Load moments
    loadMoments(state) {
      state.loading.moments = true;
      state.error.moments = null;
    },
    momentsLoaded(state, action: PayloadAction<Moment[]>) {
      state.moments = action.payload;
      state.loading.moments = false;
    },
    momentsLoadError(state, action: PayloadAction<string>) {
      state.error.moments = action.payload;
      state.loading.moments = false;
    },

    // Create moment
    createMoment(
      state,
      action: PayloadAction<Omit<Moment, 'id' | 'createdAt'>>
    ) {
      state.loading.moments = true;
      state.error.moments = null;
    },
    momentCreated(state, action: PayloadAction<Moment>) {
      state.moments.push(action.payload);
      state.loading.moments = false;
    },
    momentCreateError(state, action: PayloadAction<string>) {
      state.error.moments = action.payload;
      state.loading.moments = false;
    },

    // Load moment types
    loadMomentTypes(state) {
      state.loading.momentTypes = true;
      state.error.momentTypes = null;
    },
    momentTypesLoaded(state, action: PayloadAction<MomentType[]>) {
      state.momentTypes = action.payload;
      state.loading.momentTypes = false;
    },
    momentTypesLoadError(state, action: PayloadAction<string>) {
      state.error.momentTypes = action.payload;
      state.loading.momentTypes = false;
    },

    // Select moment type
    selectMomentType(state, action: PayloadAction<string>) {
      state.selectedMomentType = action.payload;

      // If we have cached form fields for this moment type, use them
      if (state.formFieldsCache[action.payload]) {
        state.formFields = state.formFieldsCache[action.payload];
      } else {
        // Otherwise, clear the current form fields to avoid showing wrong form
        state.formFields = [];
      }
    },

    // Load form fields
    loadFormFields(state, action: PayloadAction<string>) {
      // Only set loading state if we don't have cached data
      if (!state.formFieldsCache[action.payload]) {
        state.loading.formFields = true;
      }
      state.error.formFields = null;
    },
    formFieldsLoaded(
      state,
      action: PayloadAction<{ momentTypeId: string; fields: FormField[] }>
    ) {
      const { momentTypeId, fields } = action.payload;
      // Store in cache
      state.formFieldsCache[momentTypeId] = fields;
      // Update current form fields if this is the currently selected type
      if (state.selectedMomentType === momentTypeId) {
        state.formFields = fields;
      }
      state.loading.formFields = false;
    },
    formFieldsLoadError(state, action: PayloadAction<string>) {
      state.error.formFields = action.payload;
      state.loading.formFields = false;
    },

    // Clear cache (useful for testing or forced refresh)
    clearFormFieldsCache(state) {
      state.formFieldsCache = {};
    },

    // Clear specific cache entry
    clearFormFieldsCacheEntry(state, action: PayloadAction<string>) {
      const momentTypeId = action.payload;
      if (state.formFieldsCache[momentTypeId]) {
        delete state.formFieldsCache[momentTypeId];
      }
    },

    // Load form configs
    loadAllFormConfigs(state) {
      state.loading.allFormConfigs = true;
      state.error.allFormConfigs = null;
    },
    allFormConfigsLoaded(state, action: PayloadAction<any>) {
      state.allFormConfigs = action.payload;
      state.loading.allFormConfigs = false;
    },
    allFormConfigsLoadError(state, action: PayloadAction<string>) {
      state.error.allFormConfigs = action.payload;
      state.loading.allFormConfigs = false;
    },
    // Add this action to trigger the saga for loading both moment types and all form configs
    loadCreateFormData(state) {
      state.loading.momentTypes = true;
      state.loading.allFormConfigs = true;
      state.error.momentTypes = null;
      state.error.allFormConfigs = null;
    }
  }
});

export const { actions: rsdFormsActions, reducer: rsdFormsReducer } =
  rsdFormsSlice;

// Hook to inject the slice into the main Redux store
export const useRsdFormsSlice = () => {
  useInjectReducer({ key: rsdFormsSlice.name, reducer: rsdFormsSlice.reducer });
  useInjectSaga({ key: rsdFormsSlice.name, saga: rsdFormsSaga });
  return { actions: rsdFormsActions };
};
